import React from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, Shield, Mail, Calendar, FileText, BookOpen,
  Mic2, ChevronLeft, ChevronRight, ExternalLink, LogOut, ScanLine, DollarSign
} from 'lucide-react';

/**
 * Professional vertical sidebar navigation for the Admin Portal.
 * Grouped by section, collapsible, dark theme with pink accent.
 */

const NAV_SECTIONS = [
  {
    id: 'main',
    labels: { fr: 'TABLEAU DE BORD', en: 'DASHBOARD' },
    items: [
      { id: 'overview', icon: LayoutDashboard, labels: { fr: 'Vue d\'ensemble', en: 'Overview' } },
    ],
  },
  {
    id: 'people',
    labels: { fr: 'PERSONNES', en: 'PEOPLE' },
    items: [
      { id: 'registrations', icon: FileText, labels: { fr: 'Inscriptions', en: 'Registrations' }, statKey: 'total' },
      { id: 'manageUsers', icon: Users, labels: { fr: 'Utilisateurs', en: 'Users' } },
      { id: 'invitations', icon: Mail, labels: { fr: 'Invitations visa', en: 'Visa Invitations' } },
      { id: 'checkin', icon: ScanLine, labels: { fr: 'Pointage QR', en: 'QR Check-in' } },
      { id: 'partnerPayouts', icon: DollarSign, labels: { fr: 'Commissions partenaires', en: 'Partner Payouts' } },
    ],
  },
  {
    id: 'content',
    labels: { fr: 'CONTENU CONFÉRENCE', en: 'CONFERENCE CONTENT' },
    items: [
      { id: 'manageSpeakers', icon: Mic2, labels: { fr: 'Conférenciers', en: 'Speakers' } },
      { id: 'manageProgram', icon: Calendar, labels: { fr: 'Programme', en: 'Program' } },
      { id: 'schedules', icon: Calendar, labels: { fr: 'Horaires participants', en: 'Attendee Schedules' } },
      { id: 'documents', icon: FileText, labels: { fr: 'Documents', en: 'Documents' } },
      { id: 'resources', icon: BookOpen, labels: { fr: 'Ressources', en: 'Resources' } },
    ],
  },
];

export default function AdminSidebar({
  activeTab,
  onTabChange,
  collapsed,
  onToggleCollapsed,
  language = 'fr',
  stats = {},
  onLogout,
  role = 'admin',
}) {
  const lang = language === 'en' ? 'en' : 'fr';
  const isSubAdmin = role === 'sub_admin';

  // Sub-admins get Overview + Registrations + (read/edit) Users.
  const SUB_ADMIN_ALLOWED = new Set(['overview', 'registrations', 'manageUsers']);
  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !isSubAdmin || SUB_ADMIN_ALLOWED.has(item.id)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <aside
      data-testid="admin-sidebar"
      className={`shrink-0 h-screen sticky top-0 bg-[#0B0E13] text-gray-300 flex flex-col border-r border-white/5 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo row */}
      <div className="px-4 py-4 flex items-center gap-2.5 border-b border-white/5 h-16 shrink-0">
        <Link to="/" className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-lg ${
            isSubAdmin
              ? 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-amber-500/30'
              : 'bg-gradient-to-br from-[#CD0000] to-[#FF3333] shadow-[#CD0000]/30'
          }`}>
            <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500 leading-none font-semibold tracking-wider">{isSubAdmin ? 'SUB-ADMIN' : 'ADMIN'}</p>
              <p className="text-[13px] font-bold text-white leading-tight truncate">TALITHA KOUMI</p>
            </div>
          )}
        </Link>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-5">
        {visibleSections.map((section) => (
          <div key={section.id}>
            {!collapsed && (
              <p className="text-[10px] font-bold text-gray-500 tracking-[0.14em] mb-1.5 px-2">
                {section.labels[lang]}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onTabChange(item.id)}
                    data-testid={`admin-sidebar-${item.id}`}
                    title={collapsed ? item.labels[lang] : undefined}
                    className={`group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all relative ${
                      isActive
                        ? 'bg-white/10 text-white shadow-inner'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[#CD0000] rounded-r" />
                    )}
                    <item.icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-[#FF3333]' : ''}`} strokeWidth={2} />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left truncate">{item.labels[lang]}</span>
                        {item.statKey && stats[item.statKey] !== undefined && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                            isActive ? 'bg-white/20' : 'bg-white/5 text-gray-500 group-hover:text-gray-300'
                          }`}>
                            {stats[item.statKey]}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer: public site + collapse + logout */}
      <div className="border-t border-white/5 px-2.5 py-2.5 space-y-0.5 shrink-0">
        <Link
          to="/"
          data-testid="admin-sidebar-public-site"
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-gray-400 hover:bg-white/5 hover:text-white transition-all"
          title={collapsed ? (lang === 'fr' ? 'Site public' : 'Public site') : undefined}
        >
          <ExternalLink className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{lang === 'fr' ? 'Site public' : 'Public site'}</span>}
        </Link>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-sidebar-logout"
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-gray-400 hover:bg-white/5 hover:text-red-300 transition-all"
          title={collapsed ? (lang === 'fr' ? 'Déconnexion' : 'Sign out') : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{lang === 'fr' ? 'Déconnexion' : 'Sign out'}</span>}
        </button>
        <button
          type="button"
          onClick={onToggleCollapsed}
          data-testid="admin-sidebar-toggle"
          className="w-full flex items-center justify-center py-2 rounded-lg text-gray-500 hover:bg-white/5 hover:text-white transition-all mt-1 border-t border-white/5 pt-2"
          title={collapsed ? (lang === 'fr' ? 'Déployer' : 'Expand') : (lang === 'fr' ? 'Réduire' : 'Collapse')}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>
    </aside>
  );
}
